import { AlertTriangle } from "lucide-react";
import type { MediaResponse } from "@ckgo2/shared/types";

interface DuplicateWarningProps {
  /** Visually-similar existing media returned by POST /upload/check-duplicates. */
  matches: MediaResponse[];
  /** Use an existing media item instead of uploading the new file. */
  onUseExisting: (media: MediaResponse) => void;
  /** Dismiss the warning and proceed with the upload anyway. */
  onDismiss?: () => void;
}

/**
 * Banner shown in the MediaPicker upload flow when a near-duplicate already exists
 * in the library. Lets the editor reuse the existing asset (preferred) or dismiss
 * and upload anyway.
 */
export default function DuplicateWarning({
  matches,
  onUseExisting,
  onDismiss,
}: DuplicateWarningProps) {
  if (matches.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Similar image{matches.length > 1 ? "s" : ""} already in the library
          </p>
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
            Reuse an existing image to avoid duplicates, or upload anyway.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onUseExisting(m)}
                title={m.alt || m.originalName || m.id}
                className="group relative h-16 w-16 overflow-hidden rounded border-2 border-transparent hover:border-amber-500"
              >
                <img src={m.thumbUrl} alt={m.alt} className="h-full w-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100">
                  Use this
                </span>
              </button>
            ))}
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="mt-2 text-xs font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-400"
            >
              Upload anyway
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
